import { useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { readRedirectParam } from "@/utils/auth-redirect";
import { GoogleSignInButton } from "./google-sign-in-button";

interface MagicLinkFormProps {
  email: string;
  setEmail: (email: string) => void;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  onSuccess: () => void;
}

export function MagicLinkForm({
  email,
  setEmail,
  isLoading,
  setIsLoading,
  onSuccess
}: MagicLinkFormProps) {
  
  const handleMagicLinkLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      // Always redirect back to the current host (preview or production)
      const savedRedirect = readRedirectParam();
      if (savedRedirect) {
        sessionStorage.setItem("postAuthRedirect", savedRedirect);
      }
      // Carry the destination in the callback URL too, so the magic link still
      // returns the user to the right page when opened in another browser.
      const redirectTo = savedRedirect
        ? `${window.location.origin}/auth/callback?redirect=${encodeURIComponent(savedRedirect)}`
        : `${window.location.origin}/auth/callback`;
      const originSite = window.location.hostname;

      console.log("Setting magic link redirect URL to:", redirectTo);

      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: redirectTo,
          data: {
            originSite
          }
        }
      });
      
      if (error) {
        throw error;
      }
      
      const { pulse } = await import("@/utils/pulse");
      pulse("magic_link_sent");

      toast.success("Magic link sent!", {
        description: `We've sent a login link to ${email}. Check your inbox to continue.`,
      });
      
      setEmail("");
      onSuccess();
    } catch (error: any) {
      console.error("Error sending magic link:", error);
      
      // Check if this is a webhook timeout - email was likely still sent
      const isWebhookTimeout = error.message?.includes('failed to reach hook') || 
                               error.message?.includes('hook timeout') ||
                               error.message?.includes('timeout');
      
      if (isWebhookTimeout) {
        // Email was likely sent despite timeout
        toast.success("Magic link sent!", {
          description: `We've sent a login link to ${email}. Check your inbox (it may take a moment to arrive).`,
        });
        setEmail("");
        onSuccess();
      } else {
        toast.error("Failed to send magic link", {
          description: error.message || "An error occurred. Please try again.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleMagicLinkLogin} className="space-y-4">
      <div className="space-y-2">
        <label 
          htmlFor="email-magic" 
          className="block text-sm font-medium"
        >
          Email
        </label>
        <div className="relative">
          <Input
            id="email-magic"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="coach@example.com"
            className="w-full ps-10"
            required
          />
          <Mail className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </div>
      </div>
      
      <Button 
        type="submit" 
        className="w-full"
        disabled={isLoading}
      >
        {isLoading ? "Sending magic link..." : "Send Magic Link"}
      </Button>
      
      <div className="mt-2 text-center text-sm text-muted-foreground">
        We'll email you a magic link for a password-free login
      </div>
      
      <GoogleSignInButton />
    </form>
  );
}
